import { faEye } from '@fortawesome/pro-light-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  Badge,
  Pagination,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableFooter,
  TableHeader,
  TableRow,
} from '@windmill/react-ui';
import React, { FunctionComponent, useCallback, useState } from 'react';

import { SearchBar } from '../../common/components/SearchBar';
import { AppStatus, AppType } from '../../common/enums';
import { Link } from '../../common/Link';

type ApplicationRowProps = Application;

interface ApplicationTableProps {
  applications: Application[];
}

const AppTypeBadge: FunctionComponent<{ type: AppType }> = ({ type }) => {
  let textColor;
  switch (type) {
    case AppType.backend:
      textColor = 'text-brand-blue';
      break;
    case AppType.web:
      textColor = 'text-purple-500';
      break;
    case AppType.mobile:
      textColor = 'text-cyan-500';
  }

  return (
    <span
      className={`${textColor} rounded-full py-0.5 px-2 bg-indigo-100 font-medium uppercase text-xs`}
    >
      {type}
    </span>
  );
};

const getBadgeTypeByAppStatus = (status: string) => {
  switch (status) {
    case 'REJECTED':
      return 'danger';
    case 'DEVELOPMENT':
    case 'IN-REVIEW':
      return 'warning';
    case 'APPROVED':
    case 'LIVE':
      return 'success';
    case 'DEPRECATED':
    default:
      return 'neutral';
  }
};
const AppStatusBadge: FunctionComponent<{ status: AppStatus }> = ({
  status,
}) => {
  return (
    <Badge className="mb-px" type={`${getBadgeTypeByAppStatus(status)}`}>
      {status}
    </Badge>
  );
};

const ApplicationRow: FunctionComponent<ApplicationRowProps> = ({
  listingName,
  appType,
  status,
  appId,
}) => {
  return (
    <TableRow data-testid={`row-${listingName}`}>
      <TableCell className="py-6">
        <Link className="underline" to={`/application/${appId}/summary`}>
          {listingName}
        </Link>
      </TableCell>
      <TableCell>
        <AppTypeBadge type={appType} />
      </TableCell>
      <TableCell>
        <AppStatusBadge status={status} />
      </TableCell>
      <TableCell className="pl-14">
        <Link to={`/application/${appId}/credentials`}>
          <FontAwesomeIcon icon={faEye} />
        </Link>
      </TableCell>
    </TableRow>
  );
};

const resultsPerPage = 5;
export const ApplicationTable: React.FunctionComponent<
  ApplicationTableProps
> = ({ applications }): JSX.Element => {
  const [page, setPage] = useState(1);
  const [searchText, setSearchText] = useState('');

  const receiveSearchTextUpdate = useCallback((search: string) => {
    setSearchText(search);
  }, []);

  const filteredRows = getFilteredRowsBySearch(applications, searchText);
  const rows = filteredRows.slice(
    (page - 1) * resultsPerPage,
    page * resultsPerPage
  );

  return (
    <div>
      <SearchBar className="mb-4" onChange={receiveSearchTextUpdate} />
      <TableContainer>
        <Table>
          <TableHeader>
            <TableRow>
              <TableCell>App Name</TableCell>
              <TableCell>Type</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Credentials</TableCell>
            </TableRow>
          </TableHeader>

          <TableBody>
            {rows.length === 0 && (
              <TableRow>
                <TableCell className="p-4">No results</TableCell>
              </TableRow>
            )}
            {rows.map(({ appId, ...rest }) => {
              return (
                <ApplicationRow
                  appId={appId}
                  data-testid="app-row"
                  key={appId}
                  {...rest}
                />
              );
            })}
          </TableBody>
        </Table>
        <TableFooter>
          <Pagination
            label="Table navigation"
            onChange={setPage}
            resultsPerPage={resultsPerPage}
            totalResults={filteredRows?.length}
          />
        </TableFooter>
      </TableContainer>
    </div>
  );
};

const getFilteredRowsBySearch = (originals: Application[], search: string) => {
  if (!search) {
    return originals;
  }

  return originals.filter((app) =>
    app.listingName.toLowerCase().includes(search.toLowerCase())
  );
};
